import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../auth/authContext";
import api from "../../../../api";

export default function LiveSessionJoinForm({
  layout = "stacked",
  onSessionJoined,
  variant = "default"
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const codeInputRef = useRef(null);
  const participantNameInputRef = useRef(null);
  const [code, setCode] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const errorId = "live-session-join-error";
  const layoutClass = layout === "horizontal" ? "horizontal" : "stacked";
  const isDashboardVariant = variant === "dashboard";
  const formClasses = [
    isDashboardVariant ? "" : "form-element-container",
    "live-session-join-form",
    `live-session-join-form--${layoutClass}`,
    isDashboardVariant ? "live-session-join-form--dashboard" : "",
    isDashboardVariant ? "av-dashboard-entry-form" : ""
  ].filter(Boolean).join(" ");
  const fieldClasses = [
    "live-session-join-form__field",
    isDashboardVariant ? "av-app-field" : ""
  ].filter(Boolean).join(" ");
  const labelClassName = isDashboardVariant
    ? "av-app-field__label"
    : undefined;
  const inputClassName = isDashboardVariant
    ? "av-app-field__control"
    : "app-input";
  const submitClassName = isDashboardVariant
    ? "av-app-button av-app-button--primary av-dashboard-entry-form__submit"
    : "app-btn app-btn-primary";
  const errorMessage = error && (
    <p
      id={errorId}
      className={isDashboardVariant
        ? "av-app-field__error live-session-join-form__error"
        : undefined}
      role="alert"
      style={isDashboardVariant
        ? undefined
        : { color: "red", marginTop: "10px" }}
    >
      {error}
    </p>
  );

  const joinSession = () => {
    if (!code || !participantName) {
      setError(t("error_fill_all_fields", "Please fill in all fields."));

      if (!code) {
        codeInputRef.current?.focus();
      } else {
        participantNameInputRef.current?.focus();
      }

      return;
    }

    setLoading(true);
    setError("");

    const joinData = {
      code,
      participantName,
      userId: user?._id || null,
    };

    api
      .post("/interactive-sessions/join", joinData)
      .then((response) => {
        const selectedSample = response.data.selectedSample;
        if (typeof onSessionJoined === "function") {
          onSessionJoined(
            code,
            selectedSample,
            participantName,
            response.data
          );
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error joining session:", error);
        setError(
          error?.response?.data?.message ||
            t(
              "error_joining_session",
              "There was a problem joining the session."
            )
        );
        setLoading(false);
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    joinSession();
  };

  const codeField = (
    <div className={fieldClasses}>
      <label
        className={labelClassName}
        htmlFor="live-session-code"
      >
        {t("session_code", "Session code")}
      </label>
      <input
        ref={codeInputRef}
        id="live-session-code"
        type="text"
        className={inputClassName}
        placeholder={t("session_code", "Session code")}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        disabled={loading}
        aria-describedby={error ? errorId : undefined}
      />
    </div>
  );

  const participantNameField = (
    <div className={fieldClasses}>
      <label
        className={labelClassName}
        htmlFor="live-session-participant-name"
      >
        {t("name_id", "Name/ID")}
      </label>
      <input
        ref={participantNameInputRef}
        id="live-session-participant-name"
        type="text"
        className={inputClassName}
        placeholder={t("name_id", "Name/ID")}
        value={participantName}
        onChange={(e) => setParticipantName(e.target.value)}
        disabled={loading}
        aria-describedby={error ? errorId : undefined}
      />
    </div>
  );

  const submitButton = (
    <button
      className={submitClassName}
      type="submit"
      disabled={loading}
    >
      {loading
        ? t("joining", "Joining...")
        : t("join_live_session", "Join live session")}
    </button>
  );

  if (isDashboardVariant) {
    return (
      <form
        aria-busy={loading}
        className={formClasses}
        onSubmit={handleSubmit}
      >
        {codeField}
        {participantNameField}
        {submitButton}
        {errorMessage}
      </form>
    );
  }

  return (
    <form
      aria-busy={loading}
      className={formClasses}
      onSubmit={handleSubmit}
    >
      <div className="live-session-join-form__row">
        <div className="live-session-join-form__fields">
          {codeField}
          {participantNameField}
        </div>

        {submitButton}
      </div>

      {errorMessage}
    </form>
  );
}
